<template>
  <v-app>
    <!-- Loading screen while auth is initializing or not initialized -->
    <div v-if="authStore.initializing || !authStore.initialized" class="auth-loading">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
      <p class="mt-4">Inicializando...</p>
    </div>
    
    <!-- Solo mostrar navegación si estamos autenticados y no estamos en login -->
    <template v-else-if="authStore.isAuthenticated && !isLoginRoute">
      <v-navigation-drawer
        v-model="drawer"
        :rail="rail"
        permanent
        @click="rail = false"
      >
        <v-list-item nav>
          <template v-slot:prepend>
            <v-avatar size="40">
              <v-img src="/images/logo.png" alt="Dynamo Logo"></v-img>
            </v-avatar>
          </template>
          <template v-slot:append v-if="!rail">
            <v-btn
              variant="text"
              icon="mdi-chevron-left"
              @click.stop="rail = !rail"
            ></v-btn>
          </template>
        </v-list-item>

        <v-divider></v-divider>

        <v-list density="compact" nav>
          <v-list-item
            v-for="item in menuItems"
            :key="item.title"
            :prepend-icon="item.icon"
            :title="item.title"
            :to="item.to"
            :value="item.title"
          ></v-list-item>
        </v-list>
      </v-navigation-drawer>

      <v-app-bar>
        <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
        <v-spacer></v-spacer>
        <LanguageSelector />
        <v-btn
          :icon="isDark ? 'mdi-weather-sunny' : 'mdi-weather-night'"
          @click="toggleTheme"
        ></v-btn>
        <v-btn icon="mdi-bell"></v-btn>
        <v-menu>
          <template #activator="{ props }">
            <v-btn icon="mdi-account" v-bind="props"></v-btn>
          </template>
          <v-list>
            <v-list-item
              prepend-icon="mdi-account-circle"
              :title="userStore.user?.username || 'User'"
              :subtitle="userStore.user?.email"
            ></v-list-item>
            <v-divider></v-divider>
            <v-list-item
              prepend-icon="mdi-account-details"
              :title="t('myAccount.menuTitle')"
              @click="router.push({ name: 'MyAccount' })"
            ></v-list-item>
            <v-list-item
              prepend-icon="mdi-logout"
              title="Logout"
              @click="handleLogout"
            ></v-list-item>
          </v-list>
        </v-menu>
      </v-app-bar>
    </template>

    <v-main v-if="authStore.initialized && (authStore.isAuthenticated || isLoginRoute)">
      <v-container fluid>
        <router-view :key="$route.fullPath" />
      </v-container>
    </v-main>

    <!-- Global notification for token expiration -->
    <v-snackbar
      v-model="showTokenExpiredNotification"
      color="warning"
      timeout="5000"
      location="top"
    >
      <v-icon start>mdi-alert-circle</v-icon>
      Su sesión ha expirado. Por favor, inicie sesión nuevamente.
      <template #actions>
        <v-btn
          color="white"
          variant="text"
          @click="showTokenExpiredNotification = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>

    <!-- DD-1346/DD-1352 — aviso de intentos fallidos desde el último acceso. Solo aparece si
         hubo fallidos (AC3); el "último acceso" en si vive en MyAccount.vue, siempre visible. -->
    <v-snackbar
      v-model="showLastAccessNotice"
      color="warning"
      timeout="8000"
      location="top"
    >
      <v-icon start>mdi-shield-alert</v-icon>
      {{ lastAccessNoticeText }}
      <template #actions>
        <v-btn color="white" variant="text" @click="router.push({ name: 'MyAccount' })">
          {{ t('myAccount.viewDetail') }}
        </v-btn>
        <v-btn color="white" variant="text" @click="showLastAccessNotice = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useTheme } from 'vuetify'
import { useUserStore } from './stores/user'
import { useAuthStore } from './stores/auth'
import { myAccountService } from './services/myAccountService'
import { LanguageSelector } from './components/shared'


const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const userStore = useUserStore()
const authStore = useAuthStore()

const theme = useTheme()
const isDark = computed(() => theme.global.name.value === 'dark')
const toggleTheme = () => {
  theme.global.name.value = isDark.value ? 'light' : 'dark'
  localStorage.setItem('preferred-theme', theme.global.name.value)
}

const drawer = ref(true)
const rail = ref(false)
const showTokenExpiredNotification = ref(false)

// DD-1346/DD-1352 — aviso post-login de intentos fallidos (AC3: solo si hubo alguno).
const showLastAccessNotice = ref(false)
const lastAccessNoticeText = ref('')

// Computed property para determinar si estamos en la ruta de login
const isLoginRoute = computed(() => {
  return route.name === 'Login'
})

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}

// Watch for authentication state changes to detect token expiration
watch(() => authStore.isAuthenticated, (newValue, oldValue) => {
  // If user was authenticated and now is not, and we're not on login page
  if (oldValue === true && newValue === false && route.name !== 'Login') {
    // Check if there was a token in localStorage (indicating expiration)
    const hadToken = !!localStorage.getItem('auth_token')
    if (hadToken) {
      showTokenExpiredNotification.value = true
    }
  }

  // DD-1346/DD-1352: fetch once per fresh sign-in (false → true), not on every re-render — a
  // failure here must never block the app, so it's swallowed silently (no banner, same as
  // vue-frontend's equivalent).
  if (oldValue === false && newValue === true) {
    void loadLastAccessNotice()
  }
})

async function loadLastAccessNotice() {
  try {
    const data = await myAccountService.getLastAccess()
    if (!data || data.failedAttemptsSinceLastAccess <= 0) return

    const count = data.failedAttemptsSinceLastAccess
    const date = data.lastFailedAttemptAt
      ? new Date(data.lastFailedAttemptAt * 1000).toLocaleString()
      : ''
    lastAccessNoticeText.value = count === 1
      ? t('myAccount.noticeSingular', { date })
      : t('myAccount.noticePlural', { n: count, date })
    showLastAccessNotice.value = true
  } catch (e) {
    // silent — AC3 prefers no banner over an error banner for something non-critical
  }
}

const menuItems = computed(() => [
  {
    title: t('common.dashboard'),
    icon: 'mdi-view-dashboard',
    to: '/'
  },
  {
    title: t('common.campaigns'),
    icon: 'mdi-bullhorn',
    to: '/campaigns'
  },
  {
    title: t('common.templates'),
    icon: 'mdi-file-document',
    to: '/templates'
  },
  {
    title: t('common.categories'),
    icon: 'mdi-tag',
    to: '/categories'
  },
  {
    title: t('common.advertisers'),
    icon: 'mdi-account-group',
    to: '/advertisers'
  },
  {
    title: t('channelPrices.title'),
    icon: 'mdi-currency-usd',
    to: '/channel-prices'
  },
  {
    title: t('common.accounts'),
    icon: 'mdi-bank',
    to: '/accounts'
  },
  {
    title: t('common.users'),
    icon: 'mdi-account-multiple',
    to: '/users'
  },
  {
    title: t('common.profiles'),
    icon: 'mdi-shield-account',
    to: '/profiles'
  },
  {
    title: t('common.operators'),
    icon: 'mdi-phone',
    to: '/operators'
  },
  {
    title: t('msisdnValidators.title'),
    icon: 'mdi-phone-check',
    to: '/msisdn-validators'
  },
  {
    title: t('common.countries'),
    icon: 'mdi-earth',
    to: '/countries'
  },
  {
    title: t('common.scenarioTypes'),
    icon: 'mdi-cog',
    to: '/scenario-types'
  },
  {
    title: t('creativityFieldLocks.title'),
    icon: 'mdi-form-textbox-lock',
    to: '/creativity-field-locks'
  },
  {
    title: t('policies.title'),
    icon: 'mdi-shield-account-outline',
    to: '/policies'
  },
  {
    title: t('common.services'),
    icon: 'mdi-clipboard-list-outline',
    to: '/services'
  },
  {
    title: t('tools.title'),
    icon: 'mdi-tools',
    to: '/tools'
  },
  {
    title: t('simCards.title'),
    icon: 'mdi-sim',
    to: '/sim-cards'
  },
  ...(userStore.canViewAudit ? [{
    title: t('common.auditLog'),
    icon: 'mdi-shield-search',
    to: '/audit-log'
  }] : [])
])
</script>

<style scoped>
.v-navigation-drawer {
  transition: width 0.2s ease-in-out;
}

.v-list-item .v-avatar {
  border-radius: 8px;
  overflow: hidden;
}

.v-list-item .v-img {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.auth-loading {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  background-color: rgb(var(--v-theme-background));
  z-index: 10001;
  backdrop-filter: blur(10px);
}

.auth-loading p {
  color: rgb(var(--v-theme-on-surface-variant));
  font-size: 16px;
}
</style>
